import { InProdMark } from '@/components/admin/versioned/in-prod-mark';
import { VersionPill } from '@/components/admin/versioned/version-pill';
import { ActionMenu } from '@/components/ui/action-menu';
import type { ActionMenuItem } from '@/components/ui/action-menu';
import { Button } from '@/components/ui/button';
import { Tooltip } from '@/components/ui/tooltip';
import { useIsVitrine } from '@/lib/use-instance';
import { IN_PROD_LABEL, VERSION_TAG_LABEL, versionedDay } from '@/lib/versioned-labels';
import { VITRINE_WORDING } from '@/lib/vitrine';
import type { VersionSummary, VersionTag } from '@matter_extractor/shared';
import { VERSION_TAGS } from '@matter_extractor/shared';
import { CircleArrowUp } from 'lucide-react';

/**
 * L'historique - one line per version, with the gestures of §4 (refonte
 * modulaire §4; maquette `Editeur.dc.html`).
 *
 * Five gestures, and every one of them MOVES A POINTER or reads: « Passer en
 * production », « Reculer développement », « Repartir de cette version »,
 * « Restaurer » after a retirement, and « Voir ». Nothing here edits a version,
 * because a version is immutable - the rollback of §4 « déplace un pointeur, il
 * ne copie pas une version », so the history stays linear and this list stays
 * an ordered record rather than a working surface.
 *
 * ## The hover icon, and why it is not just a shortcut
 *
 * §4: « au survol d'une ligne, une icône sobre (infobulle "Passer en
 * production") s'affiche à côté du menu et fait le geste en un clic ». Passing
 * to production is the ONE gesture an operator repeats, and folding it into a
 * menu costs two clicks and a read of four labels each time. It stays in the
 * menu as well - a gesture that exists only on hover is a gesture a keyboard
 * cannot reach, and `opacity` alone would leave it out of the tab order's
 * reach. Hence `focus-within` beside `hover`, and the icon is a real button all
 * along.
 *
 * The tooltip is the DRAWN bubble of `ui/tooltip.tsx` and no longer a native
 * `title` (issue #103). What this paragraph used to say - that a drawn bubble
 * would be the one dark surface of a light-only application - rested on a false
 * premise: the design system draws the bubble WHITE. And the native `title` is
 * painted by the browser, so on a machine set to a dark theme it came out white
 * on black with no CSS of this repo able to catch it. The accessible name stays
 * on the button and says something else - « Passer la v3 en production » against
 * the bubble's « Passer en production » - so the bubble is announced as a
 * description beside it rather than repeating the name.
 *
 * ## No diff in this lot
 *
 * §6 is explicit and gives the reason: « un critère tient sur un écran et se
 * compare à l'œil par "Voir" ; le diff se décidera avec les protocoles, qui ne
 * tiennent pas sur un écran ». So « Voir » opens a version read-only and
 * nothing compares two - and the same holds for the persona, which fits on a
 * screen too.
 */

export interface VersionHistoryProps {
  /** The `data-testid` prefix of the object: « critere » (`VersionedWording.entity`). */
  entity: string;
  versions: readonly VersionSummary[];
  /**
   * Where the two pointers sit right now, so a line knows not to offer a move
   * onto itself.
   *
   * BOTH nullable, even though §4 guarantees `développement` always sits
   * somewhere: an entity that does not exist yet has no version at all, and
   * this panel is what it renders instead of nothing (« Aucune version. La v1
   * naît au premier enregistrement. »). A non-nullable `development` here
   * forced the caller to invent an empty `VersionSummary`, which is a lie the
   * type would have kept quiet about.
   */
  production: VersionSummary | null;
  development: VersionSummary | null;
  /** True when the entity is retired: the only state where « Restaurer » is offered. */
  retired: boolean;
  /** True while a write is in flight - every gesture is closed for the duration. */
  busy: boolean;
  onView: (version: VersionSummary) => void;
  onMovePointer: (tag: VersionTag, version: VersionSummary) => void;
  onRestartFrom: (version: VersionSummary) => void;
  onRestore: () => void;
}

/**
 * Whether the move of « latest prod » onto this version goes BACK: the version is older than the
 * one the pointer sits on. Then it is « Reculer latest prod » (the gesture `pointer` of the
 * cascade, vitrine.md §4.1) and not a passage - the one move that may go back.
 */
export function movesBack(version: VersionSummary, production: VersionSummary | null): boolean {
  return production !== null && version.number < production.number;
}

/** The gestures a line offers, in the order the maquette lists them. */
function itemsFor(version: VersionSummary, props: VersionHistoryProps): readonly ActionMenuItem[] {
  const {
    production,
    development,
    retired,
    busy,
    onView,
    onMovePointer,
    onRestartFrom,
    onRestore,
  } = props;
  const items: ActionMenuItem[] = [
    { id: 'view', label: 'Voir', onSelect: () => onView(version) },
    {
      id: 'to-production',
      // « Passer en production » for a version ahead of « latest prod », « Reculer latest
      // prod » for one behind it: the same gesture on the cascade, in the two directions.
      label: movesBack(version, production)
        ? `Reculer ${VERSION_TAG_LABEL.production}`
        : 'Passer en production',
      onSelect: () => onMovePointer('production', version),
      // A pointer already on this line has nowhere to move: the server would
      // answer `no-change`, and offering the gesture anyway would make the
      // refusal the way an operator learns where the pointer is.
      disabled: busy || retired || production?.id === version.id,
    },
    {
      id: 'to-development',
      label: `Reculer ${VERSION_TAG_LABEL.development}`,
      onSelect: () => onMovePointer('development', version),
      disabled: busy || retired || development?.id === version.id,
    },
    {
      id: 'restart',
      label: 'Repartir de cette version',
      onSelect: () => onRestartFrom(version),
      disabled: busy || retired,
    },
  ];
  if (retired) {
    items.push({ id: 'restore', label: 'Restaurer', onSelect: onRestore, disabled: busy });
  }
  return items;
}

export function VersionHistory(props: VersionHistoryProps) {
  const { entity, versions, production, retired, busy, onMovePointer } = props;
  // On the vitrine the history lists what the vitrine holds, and its one
  // gesture is « Voir » (docs/specs/vitrine.md section 7, issue #192): no
  // pastille of a pointer, no pointer to move, no draft to restart from.
  const vitrine = useIsVitrine();
  // The store reads oldest first (versioning/store.ts); an operator wants the
  // latest version at the top of the list, not scrolled past every older one.
  const orderedVersions = [...versions].reverse();

  return (
    <section
      data-testid={`${entity}-historique`}
      className="space-y-1.5 rounded-md border-2 border-gray-200 bg-white p-5"
    >
      <h2 className="font-semibold text-bleu-nuit text-lg leading-7">Historique</h2>

      {versions.length === 0 ? (
        <p className="text-gray-500 text-sm italic">
          Aucune version. La v1 naît au premier enregistrement.
        </p>
      ) : (
        <>
          <p className="pb-2 text-gray-600 text-xs leading-4">
            {vitrine
              ? VITRINE_WORDING.historyHint
              : 'Au survol d’une ligne, une icône passe la version en production ; le menu porte les autres gestes.'}
          </p>
          {/* The mark is explained once, where it first appears: it is a fact of the
              version that nothing moves (vitrine.md §4.5). */}
          {!vitrine && versions.some((version) => version.sentToProdAt != null) ? (
            <p
              data-testid={`${entity}-marque-en-prod-legende`}
              className="pb-2 text-gray-600 text-xs leading-4"
            >
              « {IN_PROD_LABEL} » : la version a été envoyée sur la prod, et y reste.
            </p>
          ) : null}
          {orderedVersions.map((version, index) => {
            const alreadyProduction = production?.id === version.id;
            return (
              <div
                key={version.id}
                data-testid={`${entity}-historique-ligne`}
                /*
                  `group` plus `focus-within` and not `hover` alone: the icon
                  below is revealed by both, so a Tab that lands on it makes it
                  visible instead of moving the focus to something invisible.
                  The pointed line carries a ground of its own, which is why the
                  hover ground is only painted on the others.
                */
                className={`group -mx-2 flex items-start gap-3 rounded-sm px-2 py-2.5 ${index === 0 ? '' : 'border-gray-100 border-t'} ${version.tags.length > 0 ? 'bg-gray-50' : 'focus-within:bg-gray-50 hover:bg-gray-50'}`}
              >
                <span className="w-8 shrink-0 font-semibold text-gray-900 text-sm leading-5">
                  v{version.number}
                </span>

                <div className="min-w-0 flex-1 space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    {vitrine
                      ? null
                      : VERSION_TAGS.filter((tag) => version.tags.includes(tag)).map((tag) => (
                          <VersionPill key={tag} entity={entity} tag={tag} />
                        ))}
                    {vitrine || version.sentToProdAt == null ? null : (
                      <InProdMark entity={entity} />
                    )}
                    <span className="text-gray-600 text-xs leading-4">
                      {versionedDay(version.createdAt)} · {version.createdBy}
                      {vitrine && alreadyProduction ? ` · ${VITRINE_WORDING.playedByLinks}` : ''}
                    </span>
                  </div>
                  {/* « Sans note » in italic, because the absence of a note is
                      not a note - the slant is what keeps the eye from reading
                      it as one. */}
                  {version.note === null || version.note === '' ? (
                    <p className="text-gray-500 text-sm italic">Sans note</p>
                  ) : (
                    <p className="break-words text-gray-800 text-sm">{version.note}</p>
                  )}
                </div>

                <div className="flex shrink-0 items-center gap-1">
                  {vitrine ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      data-testid={`${entity}-historique-voir`}
                      onClick={() => props.onView(version)}
                    >
                      Voir
                    </Button>
                  ) : null}
                  {vitrine || alreadyProduction || retired ? null : (
                    <Tooltip
                      label={
                        movesBack(version, production)
                          ? `Reculer ${VERSION_TAG_LABEL.production}`
                          : 'Passer en production'
                      }
                    >
                      <button
                        type="button"
                        data-testid={`${entity}-passer-en-production`}
                        aria-label={
                          movesBack(version, production)
                            ? `Reculer ${VERSION_TAG_LABEL.production} sur la v${version.number}`
                            : `Passer la v${version.number} en production`
                        }
                        disabled={busy}
                        onClick={() => onMovePointer('production', version)}
                        /*
                          Invisible until the line is pointed or focused, then a
                          `gray-100` chip. `opacity-0` and not `hidden`: the
                          button keeps its box, so the menu beside it does not
                          jump sideways when the icon appears.
                        */
                        className="inline-flex size-9 cursor-pointer items-center justify-center rounded-md text-bleu-nuit opacity-0 transition-opacity duration-150 hover:bg-gray-100 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:text-gray-500 group-focus-within:opacity-100 group-hover:opacity-100"
                      >
                        <CircleArrowUp aria-hidden="true" className="size-4.5" />
                      </button>
                    </Tooltip>
                  )}
                  {vitrine ? null : (
                    <ActionMenu
                      label={`Gestes sur la v${version.number}`}
                      items={itemsFor(version, props)}
                      data-testid={`${entity}-historique-menu`}
                    />
                  )}
                </div>
              </div>
            );
          })}
        </>
      )}
    </section>
  );
}
